import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function NotificationBell() {
  const { user } = useAuth();
  const canAssign = user?.role === "admin" || user?.role === "manager";
  const [data, setData] = useState({ unread: 0, items: [] });
  const [riggers, setRiggers] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (canAssign) api.get("/riggers").then((r) => setRiggers(r.data)).catch(() => {});
  }, [canAssign]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openItem = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`).catch(() => {});
    }
    setOpen(false);
    load();
    if (n.job_id) navigate(`/jobs/${n.job_id}`);
    else if (n.link) navigate(n.link);
  };

  const quickAssign = async (n, riggerId) => {
    if (!riggerId) return;
    try {
      await api.put(`/jobs/${n.job_id}`, { assigned_to: riggerId });
      const r = riggers.find((x) => x.id === riggerId);
      toast.success(`Assigned to ${r?.name || "rigger"}`, {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.put(`/jobs/${n.job_id}`, { assigned_to: "" });
              toast.success("Assignment removed");
              load();
            } catch (e) {
              toast.error(apiError(e.response?.data?.detail));
            }
          },
        },
      });
      if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Couldn't assign — the job may have been assigned or removed.");
      load();
    }
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="notification-bell"
        className="relative p-2 text-zinc-600 hover:text-zinc-900 transition-colors"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {data.unread > 0 && (
          <span
            data-testid="notification-unread-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
          >
            {data.unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] shadow-xl z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 sticky top-0 bg-white">
            <span className="font-heading font-bold tracking-tight text-zinc-900">Notifications</span>
            {data.unread > 0 && (
              <button
                onClick={markAll}
                data-testid="notification-mark-all"
                className="text-xs font-bold text-brand hover:text-brand-dark"
              >
                Mark all read
              </button>
            )}
          </div>
          {data.items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-zinc-400">You're all caught up.</div>
          ) : (
            data.items.map((n) => (
              <div
                key={n.id}
                data-testid={`notification-item-${n.id}`}
                className={`px-4 py-3 border-b border-zinc-100 ${n.read ? "" : "bg-blue-50/60"}`}
              >
                <button
                  onClick={() => openItem(n)}
                  data-testid={`notification-open-${n.id}`}
                  className="w-full text-left hover:opacity-80 transition-opacity"
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-blue-600 shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-zinc-900">{n.title}</div>
                      <div className="text-xs text-zinc-600 mt-0.5">{n.message}</div>
                      <div className="text-[10px] text-zinc-400 mt-1">{timeAgo(n.created_at)}</div>
                    </div>
                  </div>
                </button>
                {canAssign && n.type === "new_unassigned_job" && n.job_id && (
                  <div className="mt-2 pl-4">
                    <select
                      value=""
                      onChange={(e) => quickAssign(n, e.target.value)}
                      data-testid={`notification-assign-${n.id}`}
                      className="text-xs border border-zinc-300 rounded-full px-2.5 py-1 bg-white text-brand font-semibold hover:border-brand focus:outline-none focus:ring-2 focus:ring-brand cursor-pointer"
                    >
                      <option value="">+ Assign a rigger</option>
                      {riggers.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
